import type { PermitApplication } from "@/api/permit_applications";
import type { ResidentAddressKind, ResidentPayload } from "@/api/residents";
import type { TrustComplaint } from "@/api/trust_complaints";

export type ResidentCreateFormState = {
  displayName: string;
  email: string;
  phone: string;
  addressLine1: string;
  city: string;
  state: string;
  postalCode: string;
  addressKind: ResidentAddressKind;
};

export function emptyResidentCreateForm(): ResidentCreateFormState {
  return {
    displayName: "",
    email: "",
    phone: "",
    addressLine1: "",
    city: "",
    state: "",
    postalCode: "",
    addressKind: "home",
  };
}

export function residentCreateFormFromPayload(payload: ResidentPayload): ResidentCreateFormState {
  const address = payload.addresses?.[0];

  return {
    displayName: payload.display_name ?? "",
    email: payload.email ?? "",
    phone: payload.phone ?? "",
    addressLine1: address?.address_line1 ?? "",
    city: address?.city ?? "",
    state: address?.state ?? "",
    postalCode: address?.postal_code ?? "",
    addressKind: address?.kind ?? "home",
  };
}

export function residentCreatePayloadFromForm(form: ResidentCreateFormState): ResidentPayload {
  const addresses =
    form.addressLine1.trim().length > 0
      ? [
          {
            kind: form.addressKind,
            address_line1: form.addressLine1.trim(),
            city: form.city.trim() || null,
            state: form.state.trim() || null,
            postal_code: form.postalCode.trim() || null,
            is_primary: true,
          },
        ]
      : undefined;

  return {
    display_name: form.displayName.trim() || undefined,
    email: form.email.trim() || null,
    phone: form.phone.trim() || null,
    addresses,
  };
}

function stringField(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function residentCreateDefaultsFromComplaint(
  complaint: TrustComplaint,
): ResidentPayload | null {
  const displayName = stringField(complaint.contact_name);
  const email = stringField(complaint.contact_email);
  const addressData = complaint.address_data;

  let addresses: ResidentPayload["addresses"];
  if (addressData) {
    const line1 =
      stringField(addressData.address) ?? stringField(addressData.formatted_address);
    if (line1) {
      addresses = [
        {
          kind: "property",
          address_line1: line1,
          city: stringField(addressData.city),
          state: stringField(addressData.state),
          postal_code: stringField(addressData.postal_code) ?? stringField(addressData.zip),
          is_primary: true,
        },
      ];
    }
  }

  if (!displayName && !email && !addresses) {
    return null;
  }

  return {
    display_name: displayName ?? undefined,
    email,
    addresses,
  };
}

export function residentCreateDefaultsFromPermit(
  application: Pick<PermitApplication, "applicant_name" | "project_address">,
): ResidentPayload | null {
  const displayName = stringField(application.applicant_name);
  const projectAddress = stringField(application.project_address);

  if (!displayName && !projectAddress) {
    return null;
  }

  return {
    display_name: displayName ?? undefined,
    addresses: projectAddress
      ? [
          {
            kind: "project_site",
            address_line1: projectAddress,
            is_primary: true,
          },
        ]
      : undefined,
  };
}

export function residentCreateFormHasIdentity(form: ResidentCreateFormState): boolean {
  return form.displayName.trim().length > 0 || form.email.trim().length > 0;
}
